/**
 * Composable for managing SAML logout functionality.
 * Handles logout requests to the backend and token cleanup.
 */

interface LogoutResult {
    success: boolean
    logoutUrl: string | null
    message: string | null
}

interface ApiError {
    message?: string
    data?: {
        message?: string
    }
}

export function useSamlLogout() {
    const { cmsRequest } = useGraphQL()
    const isLoggingOut = ref(false)
    const logoutError = ref<string | null>(null)
    const { getToken, removeToken } = useProtectedContent()

    /**
     * Get a safe, same-origin return URL
     */
    function getSafeReturnUrl(returnUrl?: string): string {
        if (!returnUrl) {
            return window.location.href // Current page
        }

        try {
            const url = new URL(returnUrl, window.location.origin)
            if (url.origin !== window.location.origin) {
                return window.location.origin // Fallback to home
            }
            return url.href
        } catch {
            return window.location.origin
        }
    }

    /**
     * Logout from SAML gate
     * @returns Promise with success status and whether redirect occurred
     */
    async function logout(params: {
        gateUid: string
        returnUrl?: string
    }): Promise<{ success: boolean; redirected: boolean }> {
        isLoggingOut.value = true
        logoutError.value = null

        try {
            const token = getToken(params.gateUid)

            if (!token) {
                removeToken(params.gateUid)
                return { success: true, redirected: false }
            }

            // Prepare safe return URL
            const safeReturnUrl = getSafeReturnUrl(params.returnUrl)

            const result = await cmsRequest<LogoutResult>({
                uri: 'authorised-content/logout',
                method: 'POST',
                body: {
                    gateUid: params.gateUid,
                    token,
                    returnUrl: safeReturnUrl,
                },
            })

            // Always clear the local token once the logout request has been accepted.
            removeToken(params.gateUid)

            // Show message if provided
            if (result.message) {
                logoutError.value = result.message
            }

            // Redirect to IdP if provided
            if (result.logoutUrl) {
                window.location.assign(result.logoutUrl)
                return { success: true, redirected: true }
            }

            return { success: result.success, redirected: false }
        } catch (error) {
            // Still clear local tokens even on error
            removeToken(params.gateUid)
            const apiError = error as ApiError
            logoutError.value = apiError.data?.message || apiError.message || 'Logout failed'
            console.error('SAML logout error:', error)
            return { success: false, redirected: false }
        } finally {
            isLoggingOut.value = false
        }
    }

    return {
        isLoggingOut: readonly(isLoggingOut),
        logoutError: readonly(logoutError),
        logout,
    }
}
